/**
 * Which way a walker faces, and which anim it plays (lifted from the shared
 * prototype engine's `sprite-facing.js` and `main.js`). The facing follows the
 * angle of the actual movement (after sliding along a wall), never the input's
 * strength, and stays put while standing. The prototype chose among three
 * drawn sheets (front, back, side) with a narrow hysteresis band at the side
 * sheet's edge (0.67 to enter, 0.61 to leave, about 4.5°); the game's sprites
 * resolve eight facings (the sprite contract), so here the walker turns to
 * the nearest of the eight directions, keeping its current one until the
 * movement leaves that direction's 45° sector by more than
 * `walk.facingHysteresis` degrees: path corners and diagonal jitter never
 * flicker between two sheets.
 */
import type { Scene } from '@ab/content-schema';

export type Facing = Scene['spawns'][number]['facing'];

/** Counter-clockwise from east, 45° apart (game axes: y is north). */
const order: readonly Facing[] = ['e', 'ne', 'n', 'nw', 'w', 'sw', 's', 'se'];
const angleOf = (f: Facing): number => (order.indexOf(f) * Math.PI) / 4;

/** The facing for a movement of (dx, dy) in game axes, given the current one. */
export const facingFor = (dx: number, dy: number, previous: Facing, hysteresisDegrees: number): Facing => {
  if (Math.hypot(dx, dy) < 1e-6) return previous;
  const angle = Math.atan2(dy, dx);
  let off = Math.abs(angle - angleOf(previous)) % (2 * Math.PI);
  if (off > Math.PI) off = 2 * Math.PI - off;
  if (off <= Math.PI / 8 + (hysteresisDegrees * Math.PI) / 180) return previous;
  const sector = ((Math.round(angle / (Math.PI / 4)) % 8) + 8) % 8;
  return order[sector] ?? previous;
};

/** The anim a walker's sprite plays: standing, walking or running (`run` falls back to `walk` where a sheet has none). */
export type WalkAnim = 'idle' | 'walk' | 'run';
export const animOf = (w: { readonly moving: boolean; readonly running: boolean }): WalkAnim => (!w.moving ? 'idle' : w.running ? 'run' : 'walk');
